/**
 * ExpeditionTab — extracted from /app/frontend/src/pages/PalGame.jsx
 * as part of the per-tab split. Behaviour identical to the
 * pre-split monolith; only the file boundary changed.
 */
import { useEffect, useState, useMemo, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useToast } from "@/context/ToastContext";
import { PageHeader, Currency, Loader, RarityBadge } from "@/components/Atoms";
import { ChilletSkull } from "@/components/ChilletSkull";
import { ChilletCoinIcon } from "@/components/ChilletCoinIcon";
import { PalPortrait, ElementChip } from "@/components/PalAssets";
import { PayoutFlash } from "@/components/PayoutFlash";
import { AchievementUnlocked } from "@/components/AchievementUnlocked";
import { MasterTrainerCinematic } from "@/components/MasterTrainerCinematic";
import { useMasterTrainers } from "@/hooks/useMasterTrainers";
import { Pill } from "@/components/palgame/Pill";
import {
  Swords, Sparkles, Zap, Compass, Trophy, Layers, Target, Castle,
  ArrowLeftRight, Map, Crosshair, Heart, ChevronLeft, BookOpen,
} from "lucide-react";
import { PalPicker, ActionWithPal, firePalgamePayout, firePalgameDefeat } from "./_shared";

export function ExpeditionTab() {
  const toast = useToast();
  const { refreshProfile } = useAuth();
  const [pals, setPals] = useState([]);
  const [status, setStatus] = useState(null);
  const [picked, setPicked] = useState([]);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const refresh = async () => {
    try {
      const [boxRes, statusRes] = await Promise.all([
        api.get("/palgame/box"),
        api.get("/palgame/expedition/status"),
      ]);
      setPals(boxRes.data.pals || []);
      setStatus(statusRes.data);
    } catch (e) {
      toast.error(apiError(e, "Failed to load expedition status"));
    }
  };

  useEffect(() => {
    refresh();
    // Live cooldown updates — admin shortening cooldown_expedition_user
    // re-fetches status so the countdown reflects the new value.
    const onCdUpdate = (e) => {
      const { key } = e.detail || {};
      if (!key || key === "cooldown_expedition_user") refresh();
    };
    window.addEventListener("chillets:cooldown-update", onCdUpdate);
    return () => window.removeEventListener("chillets:cooldown-update", onCdUpdate);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const tiredSet = useMemo(
    () => new Set((status?.tired_pals || []).map((t) => t.pal_name)),
    [status]
  );
  const cooldownRemaining = useMemo(() => {
    if (!status?.cooldown_expires) return 0;
    const exp = new Date(status.cooldown_expires.replace(" ", "T") + "Z").getTime();
    return Math.max(0, exp - Date.now());
  }, [status]);

  const togglePick = (name) => {
    setPicked((cur) => {
      if (cur.includes(name)) return cur.filter((n) => n !== name);
      if (cur.length >= 2) return cur;
      return [...cur, name];
    });
  };

  const send = async () => {
    if (picked.length !== 2) { toast.error("Pick exactly 2 pals"); return; }
    setBusy(true); setResult(null);
    try {
      const { data } = await api.post("/palgame/expedition", {
        pal1: picked[0], pal2: picked[1],
      });
      setResult(data);
      toast.success(`+${data.reward_skulls.toLocaleString()} skulls!`);
      firePalgamePayout(data.reward_skulls, "EXPEDITION");
      setPicked([]);
      refreshProfile();
      refresh();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6 fade-up" data-testid="expedition-tab">
      <div className="glass-card p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="label">Send 2 Pals on Expedition</h3>
          {cooldownRemaining > 0 && (
            <span className="text-xs text-[var(--warning)]" data-testid="expedition-cooldown">
              Cooldown · {Math.ceil(cooldownRemaining / 60000)}m
            </span>
          )}
        </div>
        <p className="text-xs text-[var(--muted)] mb-3">
          Reward: {status?.reward_min?.toLocaleString() || "?"}–{status?.reward_max?.toLocaleString() || "?"}+ skulls.
          Pals rest for 2h after each expedition.
        </p>

        {pals.length < 2 ? (
          <p className="text-sm text-[var(--muted)] py-6 text-center">
            You need at least 2 pals. Catch more in the Catch tab!
          </p>
        ) : (
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2 max-h-[420px] overflow-y-auto">
            {pals.map((p) => {
              const isPicked = picked.includes(p.pal_name);
              const isTired = tiredSet.has(p.pal_name);
              const disabled = isTired || (!isPicked && picked.length >= 2);
              return (
                <button
                  key={p.pal_name}
                  onClick={() => !isTired && togglePick(p.pal_name)}
                  disabled={disabled}
                  data-testid={`expedition-pal-pick-${p.pal_name}`}
                  className={`flex flex-col items-center rounded-md p-2 border transition-colors relative ${
                    isPicked ? "border-[var(--teal)] bg-[var(--teal-soft)]" : "border-white/5 hover:border-[var(--teal-soft)]"
                  } ${disabled ? "opacity-30 cursor-not-allowed" : ""}`}
                  title={isTired ? "Resting from a previous expedition" : ""}
                >
                  <PalPortrait name={p.pal_name} size="w-16 h-16" />
                  <div className="text-[10px] mt-1 truncate w-full text-center">{p.pal_name}</div>
                  <div className="text-[9px] text-[var(--muted)]">Lv {p.level}</div>
                  {isTired && (
                    <span className="absolute top-1 right-1 text-[9px] bg-black/70 px-1 rounded text-[var(--warning)]">💤</span>
                  )}
                </button>
              );
            })}
          </div>
        )}

        <button
          onClick={send}
          disabled={busy || picked.length !== 2 || cooldownRemaining > 0 || pals.length < 2}
          className="btn w-full mt-4"
          data-testid="expedition-send-btn"
        >
          {busy ? "Exploring…" : cooldownRemaining > 0 ? "On cooldown" : `Send Expedition (${picked.length}/2)`}
        </button>
      </div>

      <AnimatePresence>
        {result && (
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            className="glass-card p-6 border-[var(--teal)]"
            data-testid="expedition-result"
          >
            <h3 className="text-2xl mb-2 glow-teal">Expedition Complete!</h3>
            <p className="text-sm text-[var(--muted)] italic mb-4">{result.story}</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
              <Pill label="Skulls" value={`+${result.reward_skulls.toLocaleString()}`} highlight />
              <Pill label="XP each" value={`+${result.xp_gain}`} />
              <Pill label={result.team[0]} value={`Lv ${result.team_levels[result.team[0]]}`} />
              <Pill label={result.team[1]} value={`Lv ${result.team_levels[result.team[1]]}`} />
            </div>
            {Object.keys(result.level_ups).length > 0 && (
              <div className="text-sm">
                <div className="label mb-1">Level Ups</div>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(result.level_ups).map(([pal, lv]) => (
                    <span key={pal} className="text-xs px-2 py-1 rounded bg-[var(--teal-soft)] text-[var(--teal)] border border-[var(--teal)]">
                      {pal} → Lv {lv}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}


// ============================================================================
// TRADE TAB — player↔player swap of pals + currency
// ============================================================================

